import {
  IAB_CLASSIFICATION_ROUTE_IDS,
  IKIGAI_MODEL_ROUTE_IDS,
  MISSION_WORKER_MODEL_ROUTE_IDS,
  getModelRouteDefinition,
  type ModelRouteId,
} from './routes';
import {
  LLM_ROUTE_CLOUD_PROVIDERS,
  type LlmRouteExecutableProviderId,
} from './executor';
import {
  normalizeLlmProviderId,
  type ModelRouteSelection,
  type ModelRouteSelections,
} from './preferences-v2';

export interface RouteResolutionCatalogEntry {
  provider: string;
  model: string;
  pricing?: {
    inputPer1M?: number;
    outputPer1M?: number;
  } | null;
}

export interface ResolvedIabClassificationRoute {
  routeId: ModelRouteId;
  provider: LlmRouteExecutableProviderId;
  model: string;
  selectionSource: 'user' | 'default';
  reason: string;
}

export type ResolvedMissionWorkerRoute = ResolvedIabClassificationRoute;
export type ResolvedIkigaiRoute = ResolvedIabClassificationRoute;

export interface UnavailableIabClassificationRoute {
  routeId: ModelRouteId;
  reason: string;
  savedSelection?: Pick<ModelRouteSelection, 'provider' | 'model'>;
}

export type UnavailableMissionWorkerRoute = UnavailableIabClassificationRoute;
export type UnavailableIkigaiRoute = UnavailableIabClassificationRoute;

export interface IabClassificationRoutePlan {
  ok: boolean;
  routes: Partial<Record<ModelRouteId, ResolvedIabClassificationRoute>>;
  unavailable: UnavailableIabClassificationRoute[];
}

export interface MissionWorkerRoutePlan {
  ok: boolean;
  routes: Partial<Record<ModelRouteId, ResolvedMissionWorkerRoute>>;
  unavailable: UnavailableMissionWorkerRoute[];
}

export interface IkigaiRoutePlan {
  ok: boolean;
  routes: Partial<Record<ModelRouteId, ResolvedIkigaiRoute>>;
  unavailable: UnavailableIkigaiRoute[];
}

type SavedIabClassificationRoute = Omit<ResolvedIabClassificationRoute, 'routeId'>;

export interface ResolveIabClassificationRoutePlanInput {
  routeSelections: ModelRouteSelections;
  models: readonly RouteResolutionCatalogEntry[];
  authToken?: string | null;
}

export interface ResolveMissionWorkerRoutePlanInput extends ResolveIabClassificationRoutePlanInput {
  localProviderAvailability?: Partial<Record<'ollama' | 'webllm', boolean>>;
}

export type ResolveIkigaiRoutePlanInput = ResolveMissionWorkerRoutePlanInput;

const EXECUTABLE_PROVIDERS: ReadonlySet<string> = new Set([
  'openai',
  'anthropic',
  'groq',
  'deepinfra',
  'xai',
  'ollama',
  'webllm',
]);

const CLOUD_PROVIDERS: ReadonlySet<string> = new Set(LLM_ROUTE_CLOUD_PROVIDERS);

const LOCAL_PROVIDERS: ReadonlySet<string> = new Set(['ollama', 'webllm']);

function isLikelyGenerationModel(model: string): boolean {
  const lower = model.toLowerCase();
  return ![
    'embed',
    'embedding',
    'rerank',
    'whisper',
    'tts',
    'audio',
    'image',
    'vision',
    'moderation',
  ].some((token) => lower.includes(token));
}

function normalizeCatalog(models: readonly RouteResolutionCatalogEntry[]) {
  let skippedGoogleCatalogEntries = 0;
  const catalog = models
    .map((model) => {
      const provider = normalizeLlmProviderId(model.provider);
      if (!provider) return null;
      if (provider === 'google') {
        skippedGoogleCatalogEntries += 1;
        return null;
      }
      return {
        ...model,
        provider,
      };
    })
    .filter((model): model is RouteResolutionCatalogEntry & { provider: LlmRouteExecutableProviderId } => (
      model !== null
      && EXECUTABLE_PROVIDERS.has(model.provider)
      && isLikelyGenerationModel(model.model)
    ));

  if (skippedGoogleCatalogEntries > 0) {
    console.debug('[default-selector] Google/Gemini catalog entries are not executor-backed yet; skipping', {
      count: skippedGoogleCatalogEntries,
    });
  }

  return catalog;
}

function isExecutable(
  entry: RouteResolutionCatalogEntry & { provider: LlmRouteExecutableProviderId },
  authToken?: string | null,
  localProviderAvailability?: ResolveMissionWorkerRoutePlanInput['localProviderAvailability'],
): boolean {
  if (LOCAL_PROVIDERS.has(entry.provider)) {
    return localProviderAvailability?.[entry.provider as 'ollama' | 'webllm'] ?? true;
  }
  if (!CLOUD_PROVIDERS.has(entry.provider)) return true;
  return Boolean(authToken?.trim());
}

function catalogKey(provider: string, model: string): string {
  return `${provider}:${model}`;
}

function findSavedSelection(
  selection: ModelRouteSelection | undefined,
  catalogByKey: Map<string, RouteResolutionCatalogEntry & { provider: LlmRouteExecutableProviderId }>,
  authToken?: string | null,
  localProviderAvailability?: ResolveMissionWorkerRoutePlanInput['localProviderAvailability'],
): SavedIabClassificationRoute | null {
  if (!selection) return null;

  const entry = catalogByKey.get(catalogKey(selection.provider, selection.model));
  if (!entry || !isExecutable(entry, authToken, localProviderAvailability)) return null;

  return {
    provider: entry.provider,
    model: entry.model,
    selectionSource: 'user',
    reason: 'saved-v2-selection',
  };
}

function chooseDefault(
  routeId: ModelRouteId,
  catalog: Array<RouteResolutionCatalogEntry & { provider: LlmRouteExecutableProviderId }>,
  authToken?: string | null,
  localProviderAvailability?: ResolveMissionWorkerRoutePlanInput['localProviderAvailability'],
): ResolvedIabClassificationRoute | null {
  const route = getModelRouteDefinition(routeId);
  if (!route) return null;

  const executable = catalog.filter((entry) => isExecutable(entry, authToken, localProviderAvailability));
  if (executable.length === 0) return null;

  const local = executable.filter((entry) => LOCAL_PROVIDERS.has(entry.provider));
  const candidates = route.defaultSelector.preferLocal && local.length > 0 ? local : executable;

  const pricedFirst = [...candidates].sort((a, b) => {
    const aCost = (a.pricing?.inputPer1M ?? Number.POSITIVE_INFINITY)
      + (a.pricing?.outputPer1M ?? Number.POSITIVE_INFINITY);
    const bCost = (b.pricing?.inputPer1M ?? Number.POSITIVE_INFINITY)
      + (b.pricing?.outputPer1M ?? Number.POSITIVE_INFINITY);
    if (aCost !== bCost) return aCost - bCost;
    return catalogKey(a.provider, a.model).localeCompare(catalogKey(b.provider, b.model));
  });

  const selected = pricedFirst[0];
  if (!selected) return null;

  return {
    routeId,
    provider: selected.provider,
    model: selected.model,
    selectionSource: 'default',
    reason: `route-default-selector:${route.defaultSelector.strategy}`,
  };
}

export function resolveIabClassificationRoutePlan({
  routeSelections,
  models,
  authToken,
}: ResolveIabClassificationRoutePlanInput): IabClassificationRoutePlan {
  return resolveRoutePlan({
    routeIds: IAB_CLASSIFICATION_ROUTE_IDS,
    routeSelections,
    models,
    authToken,
  });
}

function resolveRoutePlan({
  routeIds,
  routeSelections,
  models,
  authToken,
  localProviderAvailability,
}: ResolveMissionWorkerRoutePlanInput & {
  routeIds: readonly ModelRouteId[];
}): IabClassificationRoutePlan {
  const catalog = normalizeCatalog(models);
  const catalogByKey = new Map(catalog.map((entry) => [catalogKey(entry.provider, entry.model), entry]));
  const routes: Partial<Record<ModelRouteId, ResolvedIabClassificationRoute>> = {};
  const unavailable: UnavailableIabClassificationRoute[] = [];

  for (const routeId of routeIds) {
    const saved = routeSelections[routeId];
    const savedRoute = findSavedSelection(saved, catalogByKey, authToken, localProviderAvailability);
    if (savedRoute) {
      routes[routeId] = {
        ...savedRoute,
        routeId,
      };
      continue;
    }

    const defaultRoute = chooseDefault(routeId, catalog, authToken, localProviderAvailability);
    if (defaultRoute) {
      routes[routeId] = defaultRoute;
      continue;
    }

    unavailable.push({
      routeId,
      reason: saved
        ? 'saved-selection-unavailable-and-no-executable-default'
        : 'no-saved-selection-and-no-executable-default',
      savedSelection: saved ? { provider: saved.provider, model: saved.model } : undefined,
    });
  }

  return {
    ok: unavailable.length === 0,
    routes,
    unavailable,
  };
}

export function resolveMissionWorkerRoutePlan({
  routeSelections,
  models,
  authToken,
  localProviderAvailability,
}: ResolveMissionWorkerRoutePlanInput): MissionWorkerRoutePlan {
  return resolveRoutePlan({
    routeIds: MISSION_WORKER_MODEL_ROUTE_IDS,
    routeSelections,
    models,
    authToken,
    localProviderAvailability,
  });
}

export function resolveIkigaiRoutePlan({
  routeSelections,
  models,
  authToken,
  localProviderAvailability,
}: ResolveIkigaiRoutePlanInput): IkigaiRoutePlan {
  return resolveRoutePlan({
    routeIds: IKIGAI_MODEL_ROUTE_IDS,
    routeSelections,
    models,
    authToken,
    localProviderAvailability,
  });
}
